import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientPortalLogin() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        const res = await axios.post(`${API}/client/register`, { ...form, email: form.email.trim() });
        localStorage.setItem('client_token', res.data.token);
        localStorage.setItem('client_name', res.data.name || form.name);
        navigate('/portal/dashboard');
      } else {
        const res = await axios.post(`${API}/client/login`, { email: form.email.trim(), password: form.password });
        localStorage.setItem('client_token', res.data.token);
        localStorage.setItem('client_name', res.data.name);
        navigate('/portal/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0608] flex items-center justify-center px-6 relative overflow-hidden" data-testid="client-portal-login">
      {/* Cinematic background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A0F12]/40 via-[#0A0608] to-[#6B1D2A]/10 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#C9A84C]/3 rounded-full blur-[200px] pointer-events-none" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: 'easeOut' }} className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-10">
          <img src="/brand/logo-icon.png" alt="DreamRoom Theaters" className="w-20 h-20 mx-auto mb-5 object-contain" />
          <h1 className="font-playfair text-2xl text-white mb-1 tracking-wide">Client Portal</h1>
          <p className="text-[#C9A84C]/50 font-manrope text-sm tracking-widest uppercase">Dream Room Theaters</p>
        </div>

        {/* Card wrapper */}
        <div className="bg-[#1A0F12]/60 backdrop-blur-sm border border-[#c9a84c]/10 rounded-sm p-8">
          {/* Tabs */}
          <div className="flex mb-8 border-b border-[#c9a84c]/15">
            <button
              onClick={() => { setMode('login'); setError(''); }}
              className={`flex-1 py-3 font-manrope text-xs uppercase tracking-widest transition-all duration-300 ${mode === 'login' ? 'text-[#C9A84C] border-b-2 border-[#C9A84C]' : 'text-white/30 hover:text-white/50'}`}
              data-testid="login-tab"
            >
              Sign In
            </button>
            <button
              onClick={() => { setMode('register'); setError(''); }}
              className={`flex-1 py-3 font-manrope text-xs uppercase tracking-widest transition-all duration-300 ${mode === 'register' ? 'text-[#C9A84C] border-b-2 border-[#C9A84C]' : 'text-white/30 hover:text-white/50'}`}
              data-testid="register-tab"
            >
              Create Account
            </button>
          </div>

          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-sm px-4 py-3 font-manrope text-sm" data-testid="auth-error">
              <p className="text-red-400">{error}</p>
              <p className="text-white/40 text-xs mt-2">
                Need help signing in?{' '}
                <a href="/contact" className="text-[#C9A84C] underline hover:text-[#D4B560] transition-colors" data-testid="login-help-link">Contact us</a>
                {' '}and we'll get you sorted.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'register' && (
              <>
                <div>
                  <label className="block text-[#D4B560]/60 font-manrope text-[10px] uppercase tracking-widest mb-2">Full Name</label>
                  <input
                    type="text" required value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full bg-[#0A0608] border border-[#c9a84c]/20 focus:border-[#C9A84C] focus:outline-none focus:ring-1 focus:ring-[#C9A84C]/30 rounded-sm px-4 py-3 text-white font-manrope text-sm placeholder:text-white/20 transition-colors"
                    placeholder="Your full name"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <label className="block text-[#D4B560]/60 font-manrope text-[10px] uppercase tracking-widest mb-2">Phone</label>
                  <input
                    type="tel" value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                    className="w-full bg-[#0A0608] border border-[#c9a84c]/20 focus:border-[#C9A84C] focus:outline-none focus:ring-1 focus:ring-[#C9A84C]/30 rounded-sm px-4 py-3 text-white font-manrope text-sm placeholder:text-white/20 transition-colors"
                    placeholder="Your phone number"
                    data-testid="register-phone-input"
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-[#D4B560]/60 font-manrope text-[10px] uppercase tracking-widest mb-2">Email</label>
              <input
                type="email" required value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full bg-[#0A0608] border border-[#c9a84c]/20 focus:border-[#C9A84C] focus:outline-none focus:ring-1 focus:ring-[#C9A84C]/30 rounded-sm px-4 py-3 text-white font-manrope text-sm placeholder:text-white/20 transition-colors"
                placeholder="you@example.com"
                data-testid="email-input"
              />
            </div>
            <div>
              <label className="block text-[#D4B560]/60 font-manrope text-[10px] uppercase tracking-widest mb-2">Password</label>
              <input
                type="password" required value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                className="w-full bg-[#0A0608] border border-[#c9a84c]/20 focus:border-[#C9A84C] focus:outline-none focus:ring-1 focus:ring-[#C9A84C]/30 rounded-sm px-4 py-3 text-white font-manrope text-sm placeholder:text-white/20 transition-colors"
                placeholder="Your password"
                data-testid="password-input"
              />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-[#C9A84C] to-[#D4B560] hover:from-[#D4B560] hover:to-[#C9A84C] text-[#0A0608] font-manrope font-bold text-xs uppercase tracking-widest py-4 rounded-sm transition-all duration-300 disabled:opacity-50 shadow-lg shadow-[#C9A84C]/10"
              data-testid="submit-btn"
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
        </div>

        <div className="mt-8 text-center">
          <a href="/" className="text-white/25 font-manrope text-xs hover:text-[#C9A84C]/60 transition-colors duration-300">&larr; Back to Website</a>
        </div>
      </motion.div>
    </div>
  );
}
